import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  CheckCircle2, 
  XCircle, 
  AlertCircle, 
  Award, 
  Calendar, 
  User, 
  Building2,
  ArrowLeft,
  Shield,
  Clock,
  Loader2,
  Download,
  FileText
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CertificateVerification() {
  const { certificateId } = useParams();
  const [verification, setVerification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const verifyCertificate = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/api/certificates/verify/${certificateId}`);
        setVerification(response.data);
      } catch (err) {
        console.error("Verification error:", err);
        setError(err.response?.data?.detail || "Failed to verify certificate");
      } finally {
        setLoading(false);
      }
    };

    if (certificateId) {
      verifyCertificate();
    }
  }, [certificateId]);

  // Download PDF handler
  const handleDownloadPDF = async () => {
    try {
      setDownloading(true);
      const response = await axios.get(
        `${API_URL}/api/certificates/${certificateId}/pdf`,
        { responseType: 'blob' }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Zodiac_Certificate_${certificateId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download error:", err);
      alert("Failed to download certificate. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center" data-testid="verification-loading">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#1E3A8A] mx-auto mb-4" />
          <p className="text-[#1E3A8A] font-medium">Verifying certificate...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4" data-testid="verification-error">
        <Card className="max-w-lg w-full border-red-200 bg-white">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Verification Error</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <Link to="/">
              <Button variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Return Home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isValid = verification?.is_valid;
  const status = verification?.status;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="certificate-verification-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#1E3A8A] to-[#991B1B] flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="font-heading font-bold text-[#1E3A8A]">ZODIAC</span>
              <span className="text-xs block text-gray-500">Workforce Intelligence Institute</span>
            </div>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Status Card */}
          <Card 
            className={`border-2 ${
              isValid 
                ? 'border-green-500 bg-gradient-to-b from-green-50 to-white' 
                : status === 'revoked'
                ? 'border-orange-500 bg-gradient-to-b from-orange-50 to-white'
                : 'border-red-500 bg-gradient-to-b from-red-50 to-white'
            }`}
            data-testid="verification-result-card"
          >
            <CardHeader className="text-center pb-4">
              {/* Status Icon */}
              <div className="flex justify-center mb-4">
                {isValid ? (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
                    className="w-20 h-20 rounded-full bg-green-500 flex items-center justify-center shadow-lg"
                    data-testid="status-valid-icon"
                  >
                    <CheckCircle2 className="w-10 h-10 text-white" />
                  </motion.div>
                ) : status === 'revoked' ? (
                  <div 
                    className="w-20 h-20 rounded-full bg-orange-500 flex items-center justify-center shadow-lg"
                    data-testid="status-revoked-icon"
                  >
                    <AlertCircle className="w-10 h-10 text-white" />
                  </div>
                ) : (
                  <div 
                    className="w-20 h-20 rounded-full bg-red-500 flex items-center justify-center shadow-lg"
                    data-testid="status-invalid-icon"
                  >
                    <XCircle className="w-10 h-10 text-white" />
                  </div>
                )}
              </div>

              {/* Status Title */}
              <CardTitle className={`text-2xl font-bold ${
                isValid 
                  ? 'text-green-700' 
                  : status === 'revoked' 
                  ? 'text-orange-700' 
                  : 'text-red-700'
              }`}>
                {isValid 
                  ? 'Certificate Verified' 
                  : status === 'revoked' 
                  ? 'Certificate Revoked' 
                  : 'Certificate Not Found'}
              </CardTitle>

              <p className="text-gray-600 mt-2" data-testid="verification-message">
                {verification?.message}
              </p>
            </CardHeader>

            {/* Certificate Details (only show if certificate exists) */}
            {(isValid || status === 'revoked') && (
              <CardContent className="pt-4 border-t border-gray-100">
                <div className="space-y-4">
                  {/* Certificate Number */}
                  {verification?.certificate_number && (
                    <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-100">
                      <div className="w-10 h-10 rounded-lg bg-[#1E3A8A]/10 flex items-center justify-center">
                        <Award className="w-5 h-5 text-[#1E3A8A]" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Certificate Number</p>
                        <p className="font-mono font-semibold text-gray-900" data-testid="certificate-number">
                          {verification.certificate_number}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Holder Name */}
                  {verification?.holder_name && (
                    <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-100">
                      <div className="w-10 h-10 rounded-lg bg-[#1E3A8A]/10 flex items-center justify-center">
                        <User className="w-5 h-5 text-[#1E3A8A]" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Certificate Holder</p>
                        <p className="font-semibold text-gray-900" data-testid="holder-name">
                          {verification.holder_name}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Program Title */}
                  {verification?.program_title && (
                    <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-100">
                      <div className="w-10 h-10 rounded-lg bg-[#991B1B]/10 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-[#991B1B]" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Program</p>
                        <p className="font-semibold text-gray-900" data-testid="program-title">
                          {verification.program_title}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Completion Date */}
                  {verification?.completion_date && (
                    <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-100">
                      <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                        <Calendar className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Completion Date</p>
                        <p className="font-semibold text-gray-900" data-testid="completion-date">
                          {new Date(verification.completion_date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Issue Date */}
                  {verification?.issue_date && (
                    <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-100">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Clock className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Issue Date</p>
                        <p className="font-semibold text-gray-900" data-testid="issue-date">
                          {new Date(verification.issue_date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Issuer */}
                  {verification?.issuer_name && (
                    <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-100">
                      <div className="w-10 h-10 rounded-lg bg-[#1E3A8A]/10 flex items-center justify-center">
                        <Shield className="w-5 h-5 text-[#1E3A8A]" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Issued By</p>
                        <p className="font-semibold text-gray-900" data-testid="issuer-name">
                          {verification.issuer_name}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            )}
          </Card>

          {/* Verification Timestamp */}
          <div className="mt-6 text-center text-sm text-gray-500">
            <p>
              Verified on {new Date(verification?.verification_timestamp).toLocaleString('en-US', {
                dateStyle: 'full',
                timeStyle: 'short'
              })}
            </p>
          </div>

          {/* Actions */}
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            {/* Download PDF button - only for valid certificates */}
            {isValid && (
              <Button 
                onClick={handleDownloadPDF}
                disabled={downloading}
                className="w-full sm:w-auto bg-[#991B1B] hover:bg-[#7F1D1D]"
                data-testid="download-pdf-btn"
              >
                {downloading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Download Certificate
                  </>
                )}
              </Button>
            )}
            <Link to="/">
              <Button variant="outline" className="w-full sm:w-auto">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Return Home
              </Button>
            </Link>
            <Link to="/programs/training-certificates">
              <Button className="w-full sm:w-auto bg-[#1E3A8A] hover:bg-[#1E40AF]">
                <Award className="w-4 h-4 mr-2" />
                View Programs
              </Button>
            </Link>
          </div>

          {/* Security Notice */}
          <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-100">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-1">About Certificate Verification</p>
                <p>
                  This verification confirms the authenticity of certificates issued by 
                  Zodiac Workforce Intelligence Institute. If you have concerns about a 
                  certificate's validity, please contact us at{' '}
                  <a href="mailto:verification@zodiacinstitute.org" className="underline">
                    verification@zodiacinstitute.org
                  </a>
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
